import React, { useMemo, useState } from 'react';
import { Empty, Input, Segmented, Tag } from 'antd';
import { ClockCircleOutlined, DownOutlined, FileDoneOutlined, LeftOutlined, RightOutlined, SearchOutlined, ToolOutlined, WarningOutlined } from '@ant-design/icons';
import { MaintenanceStatusTag, ServiceStatusTag, maintenanceStatusColor, maintenanceStatusText, normalizeMaintStatus, serviceStatusColor, serviceStatusText } from './MaintenanceStatusTags';
import { checklistTemporal, maintenanceTemporal, parseTimestamp, repairTemporal } from '../../utils/recordTemporal';

const FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Maintenance', value: 'maintenance' },
  { label: 'Repairs', value: 'repair' },
  { label: 'Operator Checklists', value: 'checklist' },
];

function present(value) {
  return value !== undefined && value !== null && value !== '';
}

function firstPresent(...values) {
  return values.find(present);
}

function formatDateTime(value) {
  if (!present(value)) return '';
  const date = parseTimestamp(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}


function searchableText(item) {
  return [
    item.label,
    item.type,
    item.title,
    item.status?.label,
    item.personLabel,
    item.person,
    item.hours,
    item.body,
    item.extra,
    item.formattedTimestamp,
    item.timestamp,
  ]
    .filter(present)
    .join(' ')
    .toLowerCase();
}

function normalizeStatus(value) {
  if (!present(value)) return '';
  return String(value).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusKey(value) {
  if (!present(value)) return '';
  return String(value).trim().replace(/[\s-]+/g, '_').toUpperCase();
}

const submittedStatusColor = {
  COMPLETED: 'green',
  COMPLETE: 'green',
  NEEDS_REVIEW: 'orange',
};

function buildSubmittedStatus(value) {
  if (!present(value)) return null;
  const rawKey = statusKey(value);
  const maintenanceKey = normalizeMaintStatus(rawKey);
  const label = serviceStatusText[rawKey] || maintenanceStatusText[maintenanceKey] || normalizeStatus(value);
  const color = serviceStatusColor[rawKey] || maintenanceStatusColor[maintenanceKey] || submittedStatusColor[rawKey] || 'default';
  return { label, color };
}

function buildChecklistStatus(record) {
  const explicitStatus = firstPresent(record.status, record.result, record.checklistStatus, record.submittedStatus);
  if (present(explicitStatus)) {
    const key = statusKey(explicitStatus);
    if (key === 'P' || key === 'PASSED' || key === 'PASS' || key === 'IN_SERVICE') {
      return buildSubmittedStatus('IN_SERVICE');
    }
    if (key === 'F' || key === 'FAILED' || key === 'FAIL' || key === 'MARK_DOWN' || key === 'DOWN') {
      return buildSubmittedStatus('DOWN');
    }
    return buildSubmittedStatus(explicitStatus);
  }

  if (!present(record.hasFailures)) return null;
  return buildSubmittedStatus(record.hasFailures ? 'DOWN' : 'IN_SERVICE');
}

function notesPreview(value) {
  if (!present(value)) return '';
  return String(value).replace(/\s+/g, ' ').trim();
}

const GENERIC_RECORD_BODY_TEXT = new Set([
  'operator checklist',
  'repair',
  'maintenance',
  'checklist completed',
  'checklist completed with failures',
  'maintenance checklist',
  'maintenance form submitted via frontend',
  'external submitted maintenance form',
  'external submitted form',
  'repair record',
]);

function normalizeRecordBody(value) {
  return notesPreview(value).toLowerCase().replace(/[.!?]+$/g, '');
}

function meaningfulRecordBody(...values) {
  return values
    .map(notesPreview)
    .find((value) => value && !GENERIC_RECORD_BODY_TEXT.has(normalizeRecordBody(value))) || '';
}

function meaningfulRecordTitle(fallback, ...values) {
  return meaningfulRecordBody(...values) || fallback;
}

function buildMaintenanceRecord(record, index, onView) {
  const temporal = maintenanceTemporal(record);
  const tech = firstPresent(record.performedByName, record.submittedByName, record.createdByName, record.technicianName, record.technician, record.technicianSignature);
  const title = meaningfulRecordTitle('Maintenance', record.maintenanceType, record.formType, record.recordType);
  const status = firstPresent(record.status, record.result, record.workOrderStatus);
  return {
    id: `maintenance-${record.id ?? index}`,
    type: 'maintenance',
    label: 'Maintenance',
    icon: <ToolOutlined />,
    title,
    timestamp: temporal.raw,
    formattedTimestamp: temporal.display,
    status: buildSubmittedStatus(status),
    personLabel: 'Technician',
    person: tech,
    hours: firstPresent(record.hourMeter, record.hoursAtCompletion, record.currentHourMeter),
    body: meaningfulRecordBody(
      record.technicianComments,
      record.technician_comments,
      record.technicianNotes,
      record.technician_notes,
      record.workPerformed,
      record.workDescription,
      record.descriptionOfWork,
      record.description_of_work,
      record.notes,
      record.details,
      record.comments,
      record.summary,
    ),
    extra: firstPresent(record.cycleResetInfo, record.resetInfo) ? `Cycle reset: ${firstPresent(record.cycleResetInfo, record.resetInfo)}` : '',
    onView: record.id ? () => onView('maintenance', record) : null,
    temporal,
    originalIndex: index,
    groupKey: temporal.groupKey,
    groupLabel: temporal.groupLabel,
  };
}

function buildRepairRecord(record, index, onView) {
  const temporal = repairTemporal(record);
  const status = firstPresent(record.repairStatus, record.status, record.workOrderStatus);
  const waiting = String(status || '').toUpperCase().includes('WAITING') || record.waitingOnParts;
  return {
    id: `repair-${record.id ?? index}`,
    type: 'repair',
    label: 'Repair',
    icon: <WarningOutlined />,
    title: meaningfulRecordTitle('Repair', record.formType, record.recordType),
    timestamp: temporal.raw,
    formattedTimestamp: temporal.display,
    status: buildSubmittedStatus(status),
    personLabel: 'Technician',
    person: firstPresent(record.technicianName, record.assigneeName, record.performedByName),
    hours: firstPresent(record.hourMeter, record.currentHourMeter),
    body: meaningfulRecordBody(
      record.issueSummary,
      record.issueDescription,
      record.issue,
      record.description,
      record.workPerformed,
      record.workDescription,
      record.resolutionNotes,
      record.notes,
      record.comments,
      record.summary,
    ),
    extra: waiting ? 'Waiting on parts' : (record.resolvedAt ? `Resolved ${formatDateTime(record.resolvedAt)}` : ''),
    onView: record.id ? () => onView('repair', record) : null,
    temporal,
    originalIndex: index,
    groupKey: temporal.groupKey,
    groupLabel: temporal.groupLabel,
  };
}

function buildChecklistRecord(record, index, onView) {
  const temporal = checklistTemporal(record);
  const failedCount = firstPresent(record.failedItemCount, record.failureCount, record.failedItemsCount);
  const status = buildChecklistStatus(record);
  return {
    id: `checklist-${record.id ?? index}`,
    type: 'checklist',
    label: 'Operator Checklist',
    icon: <FileDoneOutlined />,
    title: meaningfulRecordTitle('Operator Checklist', record.formType, record.checklistType, record.recordType),
    timestamp: temporal.raw,
    formattedTimestamp: temporal.display,
    status,
    personLabel: 'Operator',
    person: firstPresent(record.operatorName, record.performedByName, record.operatorSignature),
    hours: firstPresent(record.hourMeter, record.hoursAtSubmission, record.submittedHourMeter),
    body: meaningfulRecordBody(record.notes, record.summary, record.comment, record.comments, record.operatorComments, record.operator_comments, record.operatorNotes, record.operator_notes),
    extra: present(failedCount) ? `${failedCount} failed item${Number(failedCount) === 1 ? '' : 's'}` : (record.hasAttachments || record.photoCount ? 'Attachments included' : ''),
    onView: record.id ? () => onView('checklist', record) : null,
    temporal,
    originalIndex: index,
    groupKey: temporal.groupKey,
    groupLabel: temporal.groupLabel,
  };
}

function RecordItem({ item }) {
  const clickableProps = item.onView
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: item.onView,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            item.onView();
          }
        },
        'aria-label': `View ${item.label} details: ${item.title}`,
      }
    : {};

  return (
    <article className={`selected-unit-record-card selected-unit-record-card-${item.type}${item.onView ? ' is-clickable' : ''}`} {...clickableProps}>
      <div className="selected-unit-record-kind-cell">
        <div className="selected-unit-record-icon" aria-hidden="true">{item.icon}</div>
        <div className="selected-unit-record-topline">
          <span className="selected-unit-record-type">{item.label}</span>
          {item.status && <Tag className="selected-unit-record-status selected-unit-record-status-mobile" color={item.status.color}>{item.status.label}</Tag>}
        </div>
      </div>
      <div className="selected-unit-record-copy">
        {item.body && <p className="selected-unit-record-summary">{item.body}</p>}
        {item.body && <p className="selected-unit-record-mobile-body">{item.body}</p>}
      </div>
      <div className="selected-unit-record-time">
        {item.formattedTimestamp && <><ClockCircleOutlined /> {item.formattedTimestamp}</>}
      </div>
      <dl className="selected-unit-record-meta">
        {item.person && <div><dt>{item.personLabel}</dt><dd>{item.person}</dd></div>}
        {present(item.hours) && <div><dt>Unit hours</dt><dd>{item.hours}</dd></div>}
        {item.extra && <div><dt>Note</dt><dd>{item.extra}</dd></div>}
      </dl>
      <div className="selected-unit-record-status-cell">
        {item.status && <Tag className="selected-unit-record-status selected-unit-record-status-desktop" color={item.status.color}>{item.status.label}</Tag>}
      </div>
    </article>
  );
}

export function SelectedUnitRecordsPanel({ forklift, maintenanceRecords = [], repairRecords = [], operatorChecklists = [], onViewRecord, onClose, closeLabel = 'Back to Forklift List' }) {
  const [filter, setFilter] = useState('all');
  const [recordSearch, setRecordSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const records = useMemo(() => {
    const viewWithForkliftContext = (type, item) => onViewRecord(type, item, forklift);

    return [
      ...maintenanceRecords.map((r, i) => buildMaintenanceRecord(r, i, viewWithForkliftContext)),
      ...repairRecords.map((r, i) => buildRepairRecord(r, maintenanceRecords.length + i, viewWithForkliftContext)),
      ...operatorChecklists.map((r, i) => buildChecklistRecord(r, maintenanceRecords.length + repairRecords.length + i, viewWithForkliftContext)),
    ].sort((a, b) => {
      const day = b.temporal.dayKey.localeCompare(a.temporal.dayKey);
      if (day) return day;
      const secondary = b.temporal.secondaryMs - a.temporal.secondaryMs;
      if (secondary) return secondary;
      const type = a.type.localeCompare(b.type);
      if (type) return type;
      const id = String(a.id).localeCompare(String(b.id));
      return id || a.originalIndex - b.originalIndex;
    });
  }, [forklift, maintenanceRecords, repairRecords, operatorChecklists, onViewRecord]);

  const visibleRecords = useMemo(() => {
    const query = recordSearch.trim().toLowerCase();
    return records.filter((record) => {
      const matchesType = filter === 'all' || record.type === filter;
      const matchesSearch = !query || searchableText(record).includes(query);
      return matchesType && matchesSearch;
    });
  }, [filter, recordSearch, records]);

  const groupedRecords = useMemo(() => {
    return visibleRecords.reduce((groups, record) => {
      const existing = groups.find((group) => group.key === record.groupKey);
      if (existing) {
        existing.records.push(record);
      } else {
        groups.push({ key: record.groupKey, label: record.groupLabel, records: [record] });
      }
      return groups;
    }, []);
  }, [visibleRecords]);

  const latestGroupKey = groupedRecords[0]?.key;
  const hasSearch = recordSearch.trim().length > 0;

  const toggleGroup = (groupKey) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupKey]: !(current[groupKey] ?? groupKey === latestGroupKey),
    }));
  };

  const emptyText = hasSearch
    ? 'No records match your search.'
    : filter === 'maintenance' ? 'No maintenance records yet.' : filter === 'repair' ? 'No repair records yet.' : filter === 'checklist' ? 'No operator checklists yet.' : 'No records yet for this unit.';
  const unitLabel = forklift?.unitId || 'Unit unavailable';
  const makeModel = [forklift?.make, forklift?.model].filter(Boolean).join(' ');
  const serialNumber = firstPresent(forklift?.serialNumber);
  const hourMeter = firstPresent(forklift?.hourMeter);
  const recordCountLabel = `${records.length} record${records.length === 1 ? '' : 's'}`;
  const hourRecordLine = [present(hourMeter) ? `${hourMeter} hrs` : null, recordCountLabel].filter(Boolean).join(' · ');
  const mobileMetadata = [
    makeModel,
    serialNumber ? `Serial ${serialNumber}` : null,
    hourRecordLine,
  ].filter(Boolean);
  const metadata = [
    makeModel,
    serialNumber ? `Serial ${serialNumber}` : null,
    present(hourMeter) ? `${hourMeter} hrs` : null,
    recordCountLabel,
  ].filter(Boolean);

  return (
    <section className="maint-expanded-wrap selected-unit-records-panel" aria-label={`Records for unit ${forklift?.unitId || ''}`}>
      <div className="selected-unit-records-header-shell">
        <div className="selected-unit-records-header">
          <div className="selected-unit-records-title">
            <span className="selected-unit-records-eyebrow">Selected unit</span>
            <div className="selected-unit-records-title-row">
              <h3>{unitLabel} Records</h3>
            </div>
            <p className="selected-unit-header-meta selected-unit-header-meta-desktop" aria-label="Selected unit summary">{metadata.join(' · ')}</p>
            <div className="selected-unit-header-meta selected-unit-header-meta-mobile" aria-label="Selected unit summary">
              {mobileMetadata.map((line) => <span key={line}>{line}</span>)}
            </div>
          </div>
          <div className="selected-unit-records-actions">
            <div className="selected-unit-records-statuses selected-unit-records-statuses-desktop"><ServiceStatusTag status={forklift?.status} /><MaintenanceStatusTag status={forklift?.maintenanceStatus} /></div>
            {onClose && (
              <button
                type="button"
                className="selected-unit-back-link"
                aria-label={`Return to forklift list from unit ${forklift?.unitId || ''} records`}
                onClick={onClose}
              >
                <LeftOutlined aria-hidden="true" />
                <span className="selected-unit-back-label-full">{closeLabel}</span>
                <span className="selected-unit-back-label-mobile">Forklift List</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="selected-unit-record-controls">
        <Segmented className="selected-unit-record-filter" options={FILTERS} value={filter} onChange={setFilter} />
        <Input
          allowClear
          className="selected-unit-record-search"
          onChange={(event) => setRecordSearch(event.target.value)}
          placeholder="Search selected unit records"
          prefix={<SearchOutlined />}
          value={recordSearch}
        />
      </div>
      {visibleRecords.length === 0 ? (
        <Empty className="selected-unit-record-empty" description={emptyText} />
      ) : (
        <div className="selected-unit-record-list">
          {groupedRecords.map((group, index) => {
            const isExpanded = expandedGroups[group.key] ?? index === 0;
            return (
              <section className="selected-unit-record-month" key={group.key} aria-label={group.label}>
                <button
                  type="button"
                  className="selected-unit-record-month-heading"
                  aria-expanded={isExpanded}
                  onClick={() => toggleGroup(group.key)}
                >
                  <span>{isExpanded ? <DownOutlined /> : <RightOutlined />} {group.label}</span>
                  <span className="selected-unit-record-month-count">{group.records.length} record{group.records.length === 1 ? '' : 's'}</span>
                </button>
                {isExpanded && (
                  <div className="selected-unit-record-month-list">
                    {group.records.map((item) => <RecordItem key={item.id} item={item} />)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}
